import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { StockDescriptor } from '../../../lib/models/stock-descriptor';
import { RootState } from '../../../lib/store/index';

import { useIsMobile } from '../../../hooks';
import { UI_ROUTES } from '../../../util/routes';
import { ClickableDiv, CompanyLogo } from '../../index';

import styles from './StockRow.module.scss';

type Props = {
  stock: StockDescriptor;
  logo?: string;
  url?: string;
  isMob?: boolean;
  closeModal?: () => void;
};

const MAX_SEARCH_SYMBOLS = 23;
const MAX_SEARCH_SYMBOLS_MOBILE = 20;

const StockRow = ({ stock, logo, isMob, url, closeModal }: Props) => {
  const { isMobile } = useIsMobile();
  const query = useSelector<RootState, string>((state: RootState) => state.fundamental.stockQuery);

  const navigate = useNavigate();
  const handleRowClick = (): void => {
    const { symbol } = stock;
    navigate(`${UI_ROUTES.symbol}/${symbol}`, { replace: true });
    closeModal && closeModal();
  };

  const sliceResultString = (result: string): JSX.Element => {
    const maxLength = isMobile ? MAX_SEARCH_SYMBOLS_MOBILE : MAX_SEARCH_SYMBOLS;
    const isLong = result.length > maxLength;
    const slicedResult = result.slice(0, maxLength);

    return result.toUpperCase().startsWith(query.toUpperCase()) ? (
      <>
        <span className={styles.searchPosition}>
          {result.slice(0, Math.min(query.length, maxLength))}
        </span>
        <span>
          {result.slice(query.length, maxLength).trimEnd()}
          {isLong && '...'}
        </span>
      </>
    ) : (
      <span>
        {slicedResult.trimEnd()}
        {isLong && '...'}
      </span>
    );
  };

  return (
    <ClickableDiv className={styles.wrapperRow} onClick={handleRowClick}>
      <div className={styles.contentWrapper}>
        {(url || logo) && <CompanyLogo url={url || ''} logo={logo || ''} symbol={stock.symbol} />}
        <div className={styles.wrapperTitleAndName}>
          <div className={styles.symbolTitle}>{sliceResultString(stock.symbol)}</div>
          <div className={styles.wrapperName}>
            <div className={styles.labelMeta}>{sliceResultString(stock.companyName)}</div>
          </div>
        </div>
      </div>
    </ClickableDiv>
  );
};

export default StockRow;
